/**
 * Singleton del widget de feedback: pub/sub para abrirlo desde cualquier parte, borrador
 * persistido y — lo más importante — **la propiedad de los blobs**.
 *
 * ## Por qué los blobs viven acá y no en el state del panel
 *
 * El panel se desmonta al cerrarse. Si el audio y las capturas vivieran en `useState`, cerrar el
 * drawer sin querer **destruiría una nota de voz de 90 segundos** — que es el peor bug posible en
 * una herramienta cuyo único trabajo es no perder lo que el usuario quiso decir. Viviendo en el
 * módulo, sobreviven a cerrar/reabrir el panel y a navegar por la SPA (`App.js` no remonta), que
 * es el caso real de pérdida.
 *
 * Contrapartida asumida: la propiedad de los `objectURL` es de este módulo. El componente NUNCA
 * revoca en su unmount; sólo `resetMedia()` revoca.
 *
 * Calcado de `support_chat/SupportChatService.js`.
 */
import {revokeAll, revokeAttachment} from './feedbackFiles';

const DRAFT_KEY_PREFIX = 'dashbo_feedback_draft_v1';
const DRAFT_TTL_MS = 7 * 24 * 60 * 60 * 1000;
const DRAFT_VERSION = 1;

/**
 * La key lleva la sesión (`<userId>:<agencyId>`) y no es global.
 *
 * Con una key única, un logout + login en la misma pestaña le mostraba al usuario nuevo el
 * borrador del anterior: `LocalStorage.removeUserData()` no toca esta entrada y el logout es SPA,
 * así que nada la limpiaba. La agencia entra en la key además del usuario porque el SuperAdmin
 * cambia de agencia con un reload completo conservando su id — mismo usuario, otro contexto.
 *
 * Los borradores viejos que quedaron con la key global se ignoran solos (nadie los lee) y expiran
 * con el TTL de 7 días.
 */
function draftKeyFor(sessionKey) {
    return `${DRAFT_KEY_PREFIX}:${sessionKey || 'unknown'}`;
}

let listeners = [];
let media = {attachments: [], audio: null};

function emit(event) {
    listeners.forEach((listener) => {
        try {
            listener(event);
        } catch (_) {
            /* un listener roto no puede tumbar a los demás */
        }
    });
}

const FeedbackService = {
    // --- pub/sub --- //

    subscribe(listener) {
        listeners.push(listener);
        return () => {
            listeners = listeners.filter((l) => l !== listener);
        };
    },

    /** Abre el panel, opcionalmente con un tipo ya elegido (para CTAs desde otras pantallas). */
    open(type = null) {
        emit({kind: 'open', type});
    },

    close() {
        emit({kind: 'close'});
    },

    // --- borrador (sólo texto: los blobs no entran en localStorage) --- //

    readDraft(sessionKey) {
        try {
            const raw = window.localStorage.getItem(draftKeyFor(sessionKey));
            if (!raw) return null;
            const draft = JSON.parse(raw);
            if (!draft || draft.v !== DRAFT_VERSION) return null;
            if (!draft.updatedAt || Date.now() - draft.updatedAt > DRAFT_TTL_MS) {
                this.clearDraft(sessionKey);
                return null;
            }
            return draft;
        } catch (_) {
            // Incógnito, storage lleno o JSON corrupto: sin borrador, pero nada se rompe.
            return null;
        }
    },

    saveDraft({type, text, aiResponse, includeContext}, sessionKey) {
        try {
            window.localStorage.setItem(draftKeyFor(sessionKey), JSON.stringify({
                v: DRAFT_VERSION,
                type: type || null,
                text: text || '',
                aiResponse: aiResponse || '',
                includeContext: includeContext !== false,
                updatedAt: Date.now(),
            }));
        } catch (_) {
            /* no-op: perder el borrador no puede romper el envío */
        }
    },

    clearDraft(sessionKey) {
        try {
            window.localStorage.removeItem(draftKeyFor(sessionKey));
        } catch (_) {
            /* no-op */
        }
    },

    /**
     * Borra TODOS los borradores del widget, de cualquier sesión.
     *
     * Es lo que corre al detectar un cambio de sesión: en ese momento no sabemos con qué key se
     * guardó el borrador anterior (la sesión vieja ya no está disponible), así que se barre por
     * prefijo. Incluye la key global de las versiones anteriores del widget.
     */
    clearAllDrafts() {
        try {
            const toRemove = [];
            for (let i = 0; i < window.localStorage.length; i += 1) {
                const key = window.localStorage.key(i);
                if (key && key.startsWith(DRAFT_KEY_PREFIX)) toRemove.push(key);
            }
            toRemove.forEach((key) => window.localStorage.removeItem(key));
        } catch (_) {
            /* no-op */
        }
    },

    hasDraftContent(draft) {
        if (!draft) return false;
        return !!(draft.type || (draft.text || '').trim() || (draft.aiResponse || '').trim());
    },

    // --- media --- //

    media() {
        return media;
    },

    setAttachments(attachments) {
        media = {...media, attachments};
    },

    removeAttachment(id) {
        const target = media.attachments.find((a) => a.id === id);
        if (target) revokeAttachment(target);
        media = {...media, attachments: media.attachments.filter((a) => a.id !== id)};
        return media.attachments;
    },

    setAudio(audio) {
        // Un solo audio por envío: el anterior se revoca al reemplazarse.
        if (media.audio && media.audio !== audio) revokeAttachment(media.audio);
        media = {...media, audio};
    },

    clearAudio() {
        if (media.audio) revokeAttachment(media.audio);
        media = {...media, audio: null};
    },

    /** Revoca TODO. Se llama al enviar OK, al "empezar de nuevo" y al cambiar de sesión. */
    resetMedia() {
        revokeAll(media.attachments);
        if (media.audio) revokeAttachment(media.audio);
        media = {attachments: [], audio: null};
    },

    /**
     * Limpia todo rastro de la sesión anterior: los blobs en memoria y los borradores guardados.
     *
     * Lo llama el entry cuando detecta que cambió `<userId>:<agencyId>`, por cualquier vía (login
     * o logout SPA en esta pestaña, o en otra vía el evento `storage`). Un borrador —y menos aún
     * una captura o una nota de voz— NO puede cruzar de un usuario a otro: es el aislamiento
     * cross-agency aplicado al frontend.
     */
    resetForNewSession() {
        this.resetMedia();
        this.clearAllDrafts();
    },
};

export default FeedbackService;
